// src/pages/organization/matches/GetAllMatch.js

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMainPageTitle } from "../../../contexts/PageContext";
import ProfileImage from "../../../components/profileImage";

const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";

const GetAllMatch = () => {
  const { t } = useTranslation();
  const { updatePageTitle } = useMainPageTitle();
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter and Sort States
  const [filters, setFilters] = useState({
    eventStatus: "",
  });

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalMatches, setTotalMatches] = useState(0);

  const fetchMatches = useCallback(async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: currentPage,
        eventType: "match",
        eventStatus: filters.eventStatus,
      });

      const response = await fetch(
        `${API_URL}/event/get-all-events-by-organization-id/?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(t("errors.failedToLoad"));
      }

      const data = await response.json();
      setMatches(data.events);
      setTotalPages(data.totalPages);
      setTotalMatches(data.total);
      setError(null);
    } catch (err) {
      setError(t("errors.failedToLoad"));
      console.error("Error fetching matches:", err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters.eventStatus, t]);

  useEffect(() => {
    updatePageTitle(t("matches.title"), t("matches.allMatches"));
    fetchMatches();
  }, [fetchMatches, updatePageTitle, t]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const calculateTimeRemaining = (startDate) => {
    const now = new Date();
    const eventDate = new Date(startDate);
    const difference = eventDate - now;

    if (difference < 0) return null;

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) {
      return t("matches.daysRemaining", { count: days });
    } else if (hours > 0) {
      return t("matches.hoursRemaining", { count: hours });
    } else if (minutes > 0) {
      return t("matches.minutesRemaining", { count: minutes });
    } else {
      return t("matches.startingSoon");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-gray-600">{t("common.loading")}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="text-red-500 mb-4">{error}</div>
        <button
          onClick={fetchMatches}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          {t("common.tryAgain")}
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t("matches.allMatches")}</h1>
          <p className="text-gray-600 mt-2">
            {t("matches.totalMatches", { count: totalMatches })}
          </p>
        </div>
        <button
          onClick={() => navigate("/organization/matches/create")}
          className="bg-[#F15213] text-white px-6 py-3 rounded-lg hover:bg-[#d14711] transition-colors duration-200 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
          {t("matches.createNewMatch")}
        </button>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("matches.status")}
            </label>
            <select
              name="eventStatus"
              value={filters.eventStatus}
              onChange={handleFilterChange}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">{t("common.allStatuses")}</option>
              <option value="completed">{t("matches.completed")}</option>
              <option value="upcoming">{t("matches.upcoming")}</option>
              <option value="inprogress">{t("matches.inProgress")}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Matches Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {matches.map((match) => (
          <div
            key={match._id}
            className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
            onClick={() => navigate(`/organization/events/${match._id}`)}
          >
            {/* Card Header with Gradient Background */}
            <div className="p-6 bg-gradient-to-r from-red-500 to-orange-500">
              <div className="flex justify-between items-start">
                <h3 className="text-xl font-bold text-white">{match.name}</h3>
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    match.eventStatus === "upcoming"
                      ? "bg-white/20 text-white"
                      : match.eventStatus === "inprogress"
                      ? "bg-green-500/20 text-white"
                      : "bg-gray-500/20 text-white"
                  }`}
                >
                  {t(`matches.${match.eventStatus}`)}
                </span>
              </div>
            </div>

            <div className="p-6">
              {/* Team Information */}
              <div className="mb-6">
                {/* Team 1 */}
                <div className="flex items-center mb-4 bg-gray-50 p-4 rounded-xl border border-gray-100 hover:bg-gray-100 transition-colors duration-200">
                  <ProfileImage
                    image={match.teamLogo1 || "/default-team-logo.png"}
                    alt={match.teamName1}
                    className="w-14 h-14 rounded-full object-cover mr-4 border-2 border-white shadow-md"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900 text-lg">{match.teamName1}</h4>
                    <div className="flex space-x-2 text-sm text-gray-600 mt-1">
                      <span className="px-2 py-1 bg-gray-200 rounded-full capitalize">{match.teamType1}</span>
                      <span className="px-2 py-1 bg-gray-200 rounded-full capitalize">{match.teamGender1}</span>
                    </div>
                  </div>
                </div>

                {/* VS Indicator */}
                <div className="flex justify-center items-center my-4">
                  <div className="px-6 py-2 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-full font-bold text-lg shadow-md">
                    {t("matches.vs")}
                  </div>
                </div>

                {/* Team 2 */}
                <div className="flex items-center bg-gray-50 p-4 rounded-xl border border-gray-100 hover:bg-gray-100 transition-colors duration-200">
                  <ProfileImage
                    image={match.teamLogo2 || "/default-team-logo.png"}
                    alt={match.teamName2}
                    className="w-14 h-14 rounded-full object-cover mr-4 border-2 border-white shadow-md"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900 text-lg">{match.teamName2}</h4>
                    <div className="flex space-x-2 text-sm text-gray-600 mt-1">
                      <span className="px-2 py-1 bg-gray-200 rounded-full capitalize">{match.teamType2}</span>
                      <span className="px-2 py-1 bg-gray-200 rounded-full capitalize">{match.teamGender2}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Match Details */}
              <div className="space-y-4">
                <p className="text-gray-600 line-clamp-2 bg-gray-50 p-3 rounded-lg border border-gray-100">
                  {match.description}
                </p>
                
                <div className="flex items-center text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-100">
                  <svg className="h-5 w-5 mr-2 text-blue-500" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                    <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="font-medium">{formatDate(match.startDate)} - {formatTime(match.startDate)}</span>
                </div>

                {/* Time Remaining for upcoming matches */}
                {match.eventStatus === "upcoming" && (
                  <div className="flex items-center text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-100">
                    <svg className="h-5 w-5 mr-2 text-blue-500" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                      <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-medium text-blue-600">
                      {calculateTimeRemaining(match.startDate)}
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
                  <span className="inline-block px-4 py-2 rounded-full text-sm font-medium bg-red-100 text-red-700">
                    {t("matches.match")}
                  </span>
                  <div className="flex items-center text-gray-600">
                    <svg className="h-5 w-5 mr-1" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                      <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <span className="font-medium">{match.participants || 0} {t("matches.participants")}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {matches.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t("matches.noMatchesFound")}</h3>
          <p className="text-gray-500 mb-4">{t("matches.noMatchesMessage")}</p>
          <button
            onClick={() => navigate("/organization/matches/create")}
            className="bg-[#F15213] text-white px-6 py-2 rounded-lg hover:bg-[#d14711] transition-colors"
          >
            {t("matches.createFirstMatch")}
          </button>
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-8 space-x-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("common.previous")}
          </button>
          
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              onClick={() => handlePageChange(i + 1)}
              className={`px-4 py-2 rounded-lg transition-colors ${
                currentPage === i + 1
                  ? "bg-[#F15213] text-white"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700"
              }`}
            >
              {i + 1}
            </button>
          ))}
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("common.next")}
          </button>
        </div>
      )}
    </div>
  );
};

export default GetAllMatch;
